/**
 * Parser para archivos del producto 24T232
 * Formato de archivo: Slv-{slaveId}_{timestamp}.txt
 * 
 * Estructura del archivo:
 * - Primera línea: Header con metadata del dispositivo
 * - Secciones de datos binarios uint16 precedidas por etiquetas:
 *   - X axys:
 *   - Y axys:
 *   - Z axys:
 *   - ADC HALL:
 *   - ADC COIL:
 *   - ADC DC HALL:
 */

import zip from 'lodash.zip';

// Etiquetas que identifican cada sección de datos (incluyen CRLF final)
const DATA_LABELS = [
  '\r\nX axys:\r\n',
  '\r\nY axys:\r\n',
  '\r\nZ axys:\r\n',
  '\r\nADC HALL:\r\n',
  '\r\nADC COIL:\r\n',
  '\r\nADC DC HALL:\r\n'
];

// Nombres amigables para las series
const FIELD_NAMES = {
  '\r\nX axys:\r\n': 'X',
  '\r\nY axys:\r\n': 'Y',
  '\r\nZ axys:\r\n': 'Z',
  '\r\nADC HALL:\r\n': 'ADC_HALL',
  '\r\nADC COIL:\r\n': 'ADC_COIL',
  '\r\nADC DC HALL:\r\n': 'ADC_DC_HALL'
};

// Configuración de grids: qué campos van en cada gráfico
const GRID_CONFIG = {
  axisFields: ['X', 'Y', 'Z'],           // Grid superior (ejes magnéticos)
  adcFields: ['ADC_HALL', 'ADC_COIL', 'ADC_DC_HALL']  // Grid inferior (ADCs)
};

/**
 * Parsea el header del archivo para extraer metadata
 * Ejemplo: "LANAVA 65981296 Default V15.0.35.14-ACT Boot 17P10 V11.0.2 Slave ID 1 Device Type 24T232 V2.0.0.7 Event Type 0"
 */
function parseHeader(headerLine) {
  const metadata = {
    raw: headerLine,
    deviceType: null,
    eventType: null,
    isPeriodic: false,
    slaveId: null,
    version: null,
    serialNumber: null
  };

  // Extraer Device Type
  const deviceTypeMatch = headerLine.match(/Device Type\s+(\w+)/i);
  if (deviceTypeMatch) {
    metadata.deviceType = deviceTypeMatch[1];
  }

  // Detectar Periodic Measure
  if (/Periodic Measure/i.test(headerLine)) {
    metadata.isPeriodic = true;
  } else {
    // Extraer Event Type (solo si no es periódico)
    const eventTypeMatch = headerLine.match(/Event Type\s+(\d+)/i);
    if (eventTypeMatch) {
      metadata.eventType = parseInt(eventTypeMatch[1], 10);
    }
  }

  // Extraer Slave ID
  const slaveIdMatch = headerLine.match(/Slave ID\s+(\d+)/i);
  if (slaveIdMatch) {
    metadata.slaveId = parseInt(slaveIdMatch[1], 10);
  }

  // Extraer versión del dispositivo (después de Device Type y antes de Event Type)
  const versionMatch = headerLine.match(/Device Type\s+\w+\s+(V[\d.]+)/i);
  if (versionMatch) {
    metadata.version = versionMatch[1];
  }

  // Extraer número de serie (segundo campo después de LANAVA)
  const serialMatch = headerLine.match(/LANAVA\s+(\d+)/i);
  if (serialMatch) {
    metadata.serialNumber = serialMatch[1];
  }

  return metadata;
}

/**
 * Encuentra las posiciones de las etiquetas en el buffer
 * Usa latin1 (ISO-8859-1) para preservar el mapeo 1:1 byte->char con datos binarios
 */
function findLabelPositions(buffer) {
  const text = new TextDecoder('latin1').decode(buffer);
  const positions = [];

  for (const label of DATA_LABELS) {
    const idx = text.indexOf(label);
    if (idx !== -1) {
      positions.push({
        label,
        fieldName: FIELD_NAMES[label],
        textIndex: idx,
        dataStart: idx + label.length
      });
    }
  }

  // Ordenar por posición
  positions.sort((a, b) => a.textIndex - b.textIndex);

  // Calcular el final de cada sección (inicio de la siguiente o fin del buffer)
  for (let i = 0; i < positions.length; i++) {
    if (i < positions.length - 1) {
      positions[i].dataEnd = positions[i + 1].textIndex;
    } else {
      positions[i].dataEnd = buffer.byteLength;
    }
  }

  return positions;
}

/**
 * Extrae los datos uint16 de una sección del buffer
 */
function extractUint16Data(buffer, start, end) {
  // Los datos binarios empiezan directamente en start (el CRLF ya está incluido en la etiqueta)
  const dataLength = end - start;
  
  // Asegurar alineación a 2 bytes
  const alignedLength = Math.floor(dataLength / 2) * 2;
  
  if (alignedLength <= 0) {
    return new Int16Array(0);
  }

  // Crear una copia del buffer para asegurar alineación
  const slice = buffer.slice(start, start + alignedLength);
  return new Int16Array(slice);
}

/**
 * Parsea el archivo completo y retorna los datos estructurados
 */
function parseFile(arrayBuffer) {
  const buffer = arrayBuffer;
  
  // Decodificar con latin1 para preservar mapeo 1:1 byte->char
  const fullText = new TextDecoder('latin1').decode(buffer);
  const firstLineEnd = fullText.indexOf('\n');
  const headerLine = fullText.substring(0, firstLineEnd).trim();
  
  // Parsear metadata del header
  const metadata = parseHeader(headerLine);
  
  // Verificar que es un archivo 24T232
  if (metadata.deviceType !== '24T232') {
    return null;
  }

  // Encontrar posiciones de las etiquetas
  const labelPositions = findLabelPositions(buffer);
  
  // Extraer datos de cada sección
  const data = {
    metadata,
    fields: {}
  };

  for (const pos of labelPositions) {
    const uint16Data = extractUint16Data(buffer, pos.dataStart, pos.dataEnd);
    data.fields[pos.fieldName] = uint16Data;
  }

  return data;
}

/**
 * Genera la configuración de ECharts para visualizar los datos
 */
function createEChartsOption(parsedData) {
  const { metadata, fields } = parsedData;
  
  // Separar campos disponibles por grid
  const axisFields = GRID_CONFIG.axisFields.filter(f => fields[f] && fields[f].length > 0);
  const adcFields = GRID_CONFIG.adcFields.filter(f => fields[f] && fields[f].length > 0);
  const availableFields = [...axisFields, ...adcFields];
  
  if (availableFields.length === 0) {
    return null;
  }

  // Crear datasets separados para cada grid (cada uno con su propia longitud)
  const axisMaxLength = Math.max(...axisFields.map(f => fields[f]?.length || 0), 0);
  const adcMaxLength = Math.max(...adcFields.map(f => fields[f]?.length || 0), 0);

  // Dataset para ejes (grid 0)
  const axisDataset = [];
  for (let i = 0; i < axisMaxLength; i++) {
    const row = [i];
    for (const field of axisFields) {
      const arr = fields[field];
      row.push(arr && i < arr.length ? arr[i] : null);
    }
    axisDataset.push(row);
  }

  // Dataset para ADCs (grid 1)
  const adcDataset = [];
  for (let i = 0; i < adcMaxLength; i++) {
    const row = [i];
    for (const field of adcFields) {
      const arr = fields[field];
      row.push(arr && i < arr.length ? arr[i] : null);
    }
    adcDataset.push(row);
  }

  // Crear series para ejes (usan datasetIndex 0)
  const axisSeries = axisFields.map((name, idx) => ({
    name: name,
    type: 'line',
    large: true,
    sampling: 'lttb',
    smooth: false,
    lineStyle: { width: 1 },
    areaStyle: null,
    datasetIndex: 0,
    encode: { x: 0, y: idx + 1 },
    xAxisIndex: 0,
    yAxisIndex: 0,
  }));

  // Crear series para ADCs (usan datasetIndex 1)
  const adcSeries = adcFields.map((name, idx) => ({
    name: name,
    type: 'line',
    large: true,
    sampling: 'lttb',
    smooth: false,
    lineStyle: { width: 1 },
    areaStyle: null,
    datasetIndex: 1,
    encode: { x: 0, y: idx + 1 },
    xAxisIndex: 1,
    yAxisIndex: 1,
  }));

  const series = [...axisSeries, ...adcSeries];

  // Construir título según tipo de medida
  const measureType = metadata.isPeriodic 
    ? 'Periodic Measure' 
    : `Event Type ${metadata.eventType ?? '?'}`;

  const option = {
    title: {
      text: `24T232 - Slave ${metadata.slaveId || '?'} - ${measureType}`
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        animation: false,
        label: { backgroundColor: '#505765' }
      }
    },
    legend: {
      data: availableFields,
      top: 30
    },
    grid: [
      { left: 60, right: 60, top: 80, bottom: '55%' },   // Grid superior (ejes)
      { left: 60, right: 60, top: '55%', bottom: 60 }    // Grid inferior (ADC)
    ],
    dataZoom: [
      { type: 'inside', xAxisIndex: [0, 1] },
      { type: 'slider', xAxisIndex: [0, 1], bottom: 10 }
    ],
    xAxis: [
      { type: 'category', gridIndex: 0, boundaryGap: false },
      { type: 'category', gridIndex: 1, boundaryGap: false }
    ],
    yAxis: [
      { type: 'value', gridIndex: 0, name: 'Ejes (X,Y,Z)' },
      { type: 'value', gridIndex: 1, name: 'ADC' }
    ],
    dataset: [
      { source: axisDataset, dimensions: ['index', ...axisFields] },
      { source: adcDataset, dimensions: ['index', ...adcFields] }
    ],
    series: series
  };

  return option;
}

/**
 * Verifica si un nombre de archivo corresponde a un archivo 24T232
 * Patrón: {imei}_{date}_{time}_Slv-{id}-24T232.txt
 */
export function is24T232File(filename) {
  return /^\d+_\d+_\d+_Slv-\d+-24T232\.txt$/i.test(filename);
}

/**
 * Procesa un archivo 24T232 y retorna la configuración para ECharts
 */
export function process24T232File(arrayBuffer, filename) {
  const parsedData = parseFile(arrayBuffer);
  
  if (!parsedData) {
    console.warn('No se pudo parsear el archivo como 24T232:', filename);
    return null;
  }

  const option = createEChartsOption(parsedData);
  
  return {
    option,
    data: parsedData,
    config: {
      productID: '24T232',
      fields: Object.keys(parsedData.fields),
      gridConfig: GRID_CONFIG
    }
  };
}

export default {
  is24T232File,
  process24T232File,
  parseHeader,
  GRID_CONFIG
};
